import React, { useEffect, useState, useMemo } from 'react';
import { Loader2, Radio, Calendar, CheckCircle2, AlertCircle, Camera, Search as SearchIcon, FileSpreadsheet, Undo2, ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ZoomableImage } from '../../components/ZoomableImage';
import { api, photoURL } from '../../lib/api';
import { CurrentPrices } from './CurrentPrices';
import { toast } from 'sonner';

const PRODUCT_LABELS = { vpower: 'V-Power', super: 'Súper', regular: 'Regular', diesel: 'Diesel', ulsd: 'ULSD' };
const PRODUCT_ORDER = ['vpower', 'super', 'regular', 'diesel', 'ulsd'];
const ORIGIN_LABELS = { masivo: 'Push masivo', tracking: 'Tracking', monitor: 'Monitor', rollback: 'Rollback' };
const fmtIso = (iso) => iso ? new Date(iso).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const dateStr = (d) => d.toISOString().slice(0, 10);

const fmt = (iso) => iso ? new Date(iso).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const statusBadge = (s) => {
  const map = {
    scheduled: ['#fef3c7', '#92400e', 'Programado'],
    notified_24h: ['#fef3c7', '#92400e', '24 h antes'],
    notified_2h: ['#fed7aa', '#9a3412', '2 h antes'],
    received: ['#dbeafe', '#1e40af', 'Recibido'],
    confirmed: ['#dcfce7', '#166534', 'Confirmado'],
    escalated: ['#fee2e2', '#991b1b', 'Escalado'],
    expired: ['#e5e7eb', '#4b5563', 'Vencido'],
    below_suggested: ['#ede9fe', '#5b21b6', 'Debajo de sugerido'],
  };
  const [bg, fg, label] = map[s] || ['#e5e7eb', '#4b5563', s];
  return <span className="text-[10.5px] px-2 py-0.5 rounded-full font-medium" style={{ background: bg, color: fg }}>{label}</span>;
};

// Detalle de estaciones del push — siempre visible (extendido por default)
// showSearch: solo los pushes masivos/rollback conservan su buscador propio;
// los pushitos individuales se filtran con el buscador global (globalQuery).
const PushStations = ({ pushId, stations, onPhoto, globalQuery = '', showSearch = true }) => {
  const [q, setQ] = useState('');
  const effectiveQ = showSearch ? q : globalQuery;
  const visible = useMemo(() => {
    const t = (effectiveQ || '').trim().toLowerCase();
    if (!t) return stations;
    const f = stations.filter(sp =>
      (sp.station_name || '').toLowerCase().includes(t) || (sp.brand || '').toLowerCase().includes(t));
    // Si el push coincidió por mensaje/origen y ninguna estación destinataria matchea, mostrar todas
    if (!showSearch && f.length === 0) return stations;
    return f;
  }, [stations, effectiveQ, showSearch]);
  return (
    <div className="border-t border-[#e8e5de] bg-[#fafaf6] p-4 max-h-[420px] overflow-y-auto" data-testid={`push-stations-${pushId}`}>
      <div className="flex items-center gap-3 mb-3 sticky top-0 bg-[#fafaf6] z-10 pb-1">
        {showSearch && (
        <div className="relative flex-1 max-w-sm">
          <SearchIcon size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#a8a397]"/>
          <Input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Buscar estación por nombre o marca…"
            className="bg-white pl-8 h-8 text-[12.5px]"
            data-testid={`push-station-search-${pushId}`}
          />
        </div>
        )}
        <span className="text-[11px] text-[#8a8579] whitespace-nowrap" data-testid={`push-station-count-${pushId}`}>
          {visible.length} de {stations.length} estaciones
        </span>
      </div>
      {visible.length === 0 ? (
        <p className="text-[12px] text-[#8a8579] py-4 text-center" data-testid={`push-station-empty-${pushId}`}>
          {stations.length === 0 ? 'Sin estaciones en este push.' : `Sin estaciones que coincidan con "${effectiveQ}".`}
        </p>
      ) : (
      <table className="w-full text-[12px]">
        <thead>
          <tr className="text-left text-[10.5px] tracking-wider uppercase text-[#8a8579]">
            <th className="pb-2 font-normal">Estación</th>
            <th className="pb-2 font-normal">Marca</th>
            <th className="pb-2 font-normal">Efectivo</th>
            <th className="pb-2 font-normal">Estado</th>
            <th className="pb-2 font-normal">Confirmación</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#e8e5de]">
          {visible.map(sp => (
            <tr key={sp.id} data-testid={`sp-${sp.id}`} className={sp.status === 'below_suggested' ? 'bg-[#f5f3ff]' : ''}>
              <td className="py-2">
                <p className="font-medium">{sp.station_name}</p>
                <p className="text-[10.5px] text-[#8a8579]">
                  {PRODUCT_ORDER.filter(k => sp.final_prices && sp.final_prices[k]).slice(0, 3)
                    .map(k => `${PRODUCT_LABELS[k]}: Q${sp.final_prices[k].self}`).join(' · ')}
                </p>
                <BelowNote sp={sp}/>
              </td>
              <td className="py-2">{sp.brand}</td>
              <td className="py-2">{fmt(sp.station_effective_at)}<span className="text-[10.5px] text-[#8a8579] ml-1">(+{sp.dept_delay_hours}h)</span></td>
              <td className="py-2">{statusBadge(sp.status)}</td>
              <td className="py-2">
                {sp.received_at && <p className="flex items-center gap-1"><CheckCircle2 size={11} className="text-[#15803d]"/> Recibido {fmt(sp.received_at)}</p>}
                {sp.confirmed_at && (
                  sp.photo_url ? (
                    <button
                      onClick={() => onPhoto(sp)}
                      className="flex items-center gap-1 text-[#15803d] underline underline-offset-2 hover:text-[#166534]"
                      data-testid={`sp-photo-btn-${sp.id}`}
                    >
                      <Camera size={11}/> Foto {fmt(sp.confirmed_at)} · Ver
                    </button>
                  ) : (
                    <p className="flex items-center gap-1"><Camera size={11} className="text-[#15803d]"/> Foto {fmt(sp.confirmed_at)} <span className="text-[#8a8579]">(ya no disponible)</span></p>
                  )
                )}
                {!sp.received_at && !sp.confirmed_at && <span className="text-[#8a8579]">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      )}
    </div>
  );
};
const BelowNote = ({ sp }) => {
  if (!sp.below_suggested_products) return null;
  const items = Object.entries(sp.below_suggested_products)
    .map(([pid, v]) => `${PRODUCT_LABELS[pid] || pid}: vigente Q${v.vigente} < sug. Q${v.sugerido}`);
  const full = sp.status === 'below_suggested';
  return (
    <p className="text-[10px] text-[#5b21b6] mt-0.5" data-testid={`below-note-${sp.station_id || sp.id}`}>
      {full ? 'Recordatorio · no se le envió el cambio (a la baja y ya está por debajo del sugerido)' : 'No enviado (debajo de sugerido)'}: {items.join(' · ')}
    </p>
  );
};

const ReportStatusChip = ({ s }) => {
  const map = {
    scheduled: ['#fef3c7', '#92400e', 'Programado'], notified_24h: ['#fef3c7', '#92400e', '24h antes'],
    notified_2h: ['#fed7aa', '#9a3412', '2h antes'], received: ['#dbeafe', '#1e40af', 'Recibido'],
    confirmed: ['#dcfce7', '#166534', 'Confirmado'], escalated: ['#fee2e2', '#991b1b', 'Escalado'],
    cancelled: ['#e5e7eb', '#4b5563', 'Cancelado'], expired: ['#e5e7eb', '#4b5563', 'Vencido'], below_suggested: ['#ede9fe', '#5b21b6', 'Debajo de sugerido'],
  };
  const [bg, fg, label] = map[s] || ['#e5e7eb', '#4b5563', s];
  return <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: bg, color: fg }}>{label}</span>;
};

const PushReports = () => {
  const [subTab, setSubTab] = useState('pushes');
  const [from, setFrom] = useState(dateStr(new Date(Date.now() - 30 * 86400000)));
  const [to, setTo] = useState(dateStr(new Date()));
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDeps, setOpenDeps] = useState({});
  const [exporting, setExporting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/price-pushes/report', { params: { from_date: from, to_date: to } })
      .then(r => setReport(r.data)).catch(() => toast.error('Error cargando reporte'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const r = await api.get('/price-pushes/report/export', { params: { from_date: from, to_date: to }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `reporte_pushes_${from}_${to}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Error al exportar'); }
    finally { setExporting(false); }
  };

  const toggleDep = (k) => setOpenDeps(o => ({ ...o, [k]: !o[k] }));

  return (
    <div className="space-y-4" data-testid="push-reports-panel">
      <div className="flex gap-2">
        {[['pushes', 'Pushes enviados'], ['vigentes', 'Precios vigentes']].map(([k, label]) => (
          <button key={k} onClick={() => setSubTab(k)}
            className={`px-3.5 py-1.5 rounded-full text-[12px] border transition ${subTab === k ? 'bg-[#1c1c1c] text-white border-[#1c1c1c]' : 'bg-white text-[#5a5a5a] border-[#e8e5de] hover:border-[#1c1c1c]'}`}
            data-testid={`subtab-${k}`}>
            {label}
          </button>
        ))}
      </div>

      {subTab === 'vigentes' && <CurrentPrices/>}
      {subTab === 'pushes' && (<>
      <div className="bg-white border border-[#e8e5de] rounded-xl p-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-[#8a8579]">Desde</Label>
          <Input type="date" value={from} onChange={e => setFrom(e.target.value)} className="mt-1 bg-[#fafaf6] h-9" data-testid="report-from"/>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-[#8a8579]">Hasta</Label>
          <Input type="date" value={to} onChange={e => setTo(e.target.value)} className="mt-1 bg-[#fafaf6] h-9" data-testid="report-to"/>
        </div>
        <Button onClick={load} variant="outline" className="h-9 rounded-full text-[12.5px]" data-testid="report-apply">Aplicar</Button>
        <div className="flex-1"/>
        <Button onClick={exportXlsx} disabled={exporting || loading} className="h-9 rounded-full bg-[#15803d] hover:bg-[#166534] text-white text-[12.5px]" data-testid="report-export">
          {exporting ? <Loader2 size={13} className="animate-spin mr-1.5"/> : <FileSpreadsheet size={13} className="mr-1.5"/>} Exportar a Excel
        </Button>
      </div>

      {loading ? <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div>
        : report.length === 0 ? <p className="text-[13px] text-[#8a8579] py-6 text-center">Sin pushes en el rango seleccionado.</p>
        : report.map(p => (
        <div key={p.id} className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden" data-testid={`report-push-${p.id}`}>
          <div className="p-4 flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-[#e8e5de] bg-[#fafaf6]">
            <p className="text-[13.5px] font-medium">Enviado {fmtIso(p.created_at)}</p>
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#e0e7ff] text-[#3730a3] font-medium">{ORIGIN_LABELS[p.origin] || p.origin}</span>
            <span className="text-[11.5px] text-[#5a5a5a]">por {p.created_by_name}</span>
            {p.rolled_back_at && <span className="text-[10.5px] px-2 py-0.5 rounded-full bg-[#fee2e2] text-[#991b1b] font-medium">Con rollback aplicado</span>}
            <div className="flex-1"/>
            <span className="text-[11.5px] text-[#8a8579]">{p.confirmed_count}/{p.stations_count} confirmadas</span>
          </div>
          <div className="p-3">
            <p className="text-[10.5px] uppercase tracking-wider text-[#8a8579] mb-2">🇬🇹 Guatemala · {p.stations_count} sitio(s)</p>
            {p.departments.map(dep => {
              const k = `${p.id}-${dep.name}`;
              const open = openDeps[k] !== false;
              return (
                <div key={k} className="mb-2 border border-[#e8e5de] rounded-lg overflow-hidden">
                  <button onClick={() => toggleDep(k)} className="w-full px-3 py-2 flex items-center gap-2 bg-[#fafaf6] hover:bg-[#f0ede6] text-left" data-testid={`report-dep-${k}`}>
                    {open ? <ChevronDown size={13}/> : <ChevronRight size={13}/>}
                    <span className="text-[12.5px] font-medium">{dep.name}</span>
                    <span className="text-[11px] text-[#8a8579]">· {dep.stations.length} sitio(s)</span>
                  </button>
                  {open && (
                    <table className="w-full text-[11.5px]">
                      <thead>
                        <tr className="text-left text-[9.5px] uppercase tracking-wider text-[#8a8579]">
                          <th className="py-1.5 px-3 font-normal">Sitio</th>
                          <th className="py-1.5 px-2 font-normal">BU</th>
                          <th className="py-1.5 px-2 font-normal">Región</th>
                          <th className="py-1.5 px-2 font-normal">Precios enviados</th>
                          <th className="py-1.5 px-2 font-normal">Efectivo</th>
                          <th className="py-1.5 px-2 font-normal">Estado</th>
                          <th className="py-1.5 px-2 font-normal">Confirmado</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#f0ede6]">
                        {dep.stations.map(st => (
                          <tr key={st.station_id + p.id} className={st.status === 'below_suggested' ? 'bg-[#f5f3ff]' : ''}>
                            <td className="py-1.5 px-3">
                              <span className="font-medium">{st.station_name}</span> <span className="text-[10px] text-[#8a8579]">{st.brand}</span>
                              <BelowNote sp={st}/>
                            </td>
                            <td className="py-1.5 px-2 text-[10.5px]">{st.bu || '—'}</td>
                            <td className="py-1.5 px-2 text-[10.5px]">{st.region || '—'}</td>
                            <td className="py-1.5 px-2 font-mono tabular-nums text-[10.5px]">
                              {PRODUCT_ORDER.filter(fid => st.final_prices?.[fid]).map(fid => `${PRODUCT_LABELS[fid]}: Q${st.final_prices[fid].self ?? '—'}`).join(' · ')}
                            </td>
                            <td className="py-1.5 px-2 whitespace-nowrap">{fmtIso(st.station_effective_at)}</td>
                            <td className="py-1.5 px-2"><ReportStatusChip s={st.status}/></td>
                            <td className="py-1.5 px-2 whitespace-nowrap">{st.confirmed_at ? fmtIso(st.confirmed_at) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      </>)}
    </div>
  );
};

const AdminPushPrices = () => {
  const [pushes, setPushes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pushQuery, setPushQuery] = useState('');
  const [tmFilter, setTmFilter] = useState('all');
  const [tms, setTms] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const firstRunRef = React.useRef(true);
  const [photoSp, setPhotoSp] = useState(null);
  const [form, setForm] = useState({
    effective_date: '',
    effective_time: '',
    notify_date: '',
    notify_time: '',
    target_brands: ['UNO', 'Shell'],
    base_prices: { vpower: '', super: '', regular: '', diesel: '', ulsd: '' },
    products_on: { vpower: false, super: false, regular: false, diesel: false, ulsd: false },
    preset_note_id: '',
    message: '',
    include_do: false,
  });
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState('pushes');
  const [rollbackTarget, setRollbackTarget] = useState(null);
  const [rollingBack, setRollingBack] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const r = await api.delete(`/price-pushes/${deleteTarget.id}`);
      toast.success(`Envío eliminado · se retiró de ${r.data.stations_removed} estación(es)`);
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar', { duration: 8000 }); }
    finally { setDeleting(false); }
  };

  const doRollback = async () => {
    if (!rollbackTarget) return;
    setRollingBack(true);
    try {
      const r = await api.post(`/price-pushes/${rollbackTarget.id}/rollback`);
      toast.success(`Rollback enviado · ${r.data.stations} estaciones restauran precios anteriores`);
      setRollbackTarget(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al hacer rollback'); }
    finally { setRollingBack(false); }
  };

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/price-pushes', { params: { include_stations: 1 } }), api.get('/departments')])
      .then(([p, d]) => { setPushes(p.data); setDepartments(d.data); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get('/users')
      .then(r => setTms((r.data || []).filter(x => x.role === 'territory_manager' && x.status === 'active')))
      .catch(() => {});
  }, []);

  // Buscador de pushes por estación destinataria y filtro por TM (debounce + filtro en backend)
  useEffect(() => {
    if (firstRunRef.current) { firstRunRef.current = false; return; }
    const t = setTimeout(() => {
      setListLoading(true);
      api.get('/price-pushes', { params: {
        include_stations: 1,
        ...(pushQuery.trim() ? { station: pushQuery.trim() } : {}),
        ...(tmFilter !== 'all' ? { tm: tmFilter } : {}),
      } })
        .then(r => setPushes(r.data))
        .catch(() => {})
        .finally(() => setListLoading(false));
    }, 350);
    return () => clearTimeout(t);
  }, [pushQuery, tmFilter]);

  const allNotes = useMemo(() => {
    const out = [];
    (departments || []).forEach(d => (d.preset_notes || []).forEach(n => out.push({ ...n, dept: d.name })));
    return out;
  }, [departments]);

  const selectedNote = allNotes.find(n => n.id === form.preset_note_id);

  const toggleBrand = (b) => setForm(f => ({
    ...f,
    target_brands: f.target_brands.includes(b) ? f.target_brands.filter(x => x !== b) : [...f.target_brands, b],
  }));

  const submit = async () => {
    if (!form.effective_date || !form.effective_time) { toast.error('Selecciona fecha y hora efectiva'); return; }
    if (form.target_brands.length === 0) { toast.error('Selecciona al menos una marca'); return; }
    const prices = {};
    const on = Object.entries(form.products_on).filter(([, v]) => v).map(([k]) => k);
    if (on.length === 0) { toast.error('Marca con ✓ el/los producto(s) que tuvieron movimiento'); return; }
    for (const k of on) {
      const v = form.base_prices[k];
      if (v === '' || isNaN(parseFloat(v))) { toast.error(`Ingresa el precio base de ${PRODUCT_LABELS[k]} (está marcado)`); return; }
      prices[k] = parseFloat(v);
    }
    // Build ISO datetime in local timezone (Guatemala UTC-6). Send as local then backend parses.
    const effective_at = new Date(`${form.effective_date}T${form.effective_time}:00`).toISOString();
    let notify_at = null;
    if (form.notify_date || form.notify_time) {
      if (!form.notify_date || !form.notify_time) { toast.error('Completa fecha Y hora de notificación, o deja ambas vacías'); return; }
      notify_at = new Date(`${form.notify_date}T${form.notify_time}:00`).toISOString();
      if (new Date(notify_at) >= new Date(effective_at)) { toast.error('La notificación debe programarse ANTES de la fecha/hora efectiva'); return; }
    }
    setSending(true);
    try {
      const r = await api.post('/price-pushes', {
        effective_at,
        notify_at,
        target_brands: form.target_brands,
        base_prices: prices,
        preset_note_id: form.preset_note_id || null,
        message: form.message,
        include_do: form.include_do,
      });
      const belowN = r.data.below_suggested_count || 0;
      const doN = r.data.skipped_do_count || 0;
      toast.success(`Push creado · ${r.data.stations_count} estaciones notificadas${belowN ? ` · ${belowN} por debajo de sugerido (solo recordatorio)` : ''}${doN ? ` · ${doN} DO omitidas (sin zona TM)` : ''}`);
      const noProd = r.data.skipped_no_products_count || 0;
      if (noProd) {
        toast.warning(`${noProd} estación(es) NO recibieron el push porque no tienen habilitado ninguno de los productos enviados: ${(r.data.skipped_no_products || []).slice(0, 5).join(', ')}${noProd > 5 ? '…' : ''}. Revisa sus "Productos disponibles" en Configuración → Estaciones.`, { duration: 15000 });
      }
      const bbp = r.data.below_by_product || {};
      const bbpEntries = Object.entries(bbp);
      if (bbpEntries.length) {
        const labels = { vpower: 'V-Power', super: 'Súper', regular: 'Regular', diesel: 'Diésel', ulsd: 'ULSD' };
        toast.warning(`Productos omitidos por estar YA por debajo del sugerido: ${bbpEntries.map(([p, n]) => `${labels[p] || p} en ${n} estación(es)`).join(' · ')}. Esas estaciones no recibirán ese producto en este push.`, { duration: 15000 });
      }
      setForm({
        effective_date: '', effective_time: '', notify_date: '', notify_time: '', target_brands: ['UNO', 'Shell'],
        base_prices: { vpower: '', super: '', regular: '', diesel: '', ulsd: '' },
        products_on: { vpower: false, super: false, regular: false, diesel: false, ulsd: false },
        preset_note_id: '', message: '', include_do: false,
      });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-push-prices-page">
      <div>
        <h2 className="font-serif text-[36px] leading-tight tracking-tight">Push de precios</h2>
        <p className="text-[13px] text-[#5a5a5a] mt-1">Envía un cambio de precio general a las estaciones UNO y Shell · se aplican reglas de departamento y estación</p>
      </div>

      <div className="flex gap-1 border-b border-[#e8e5de]">
        {[['pushes', 'Pushes'], ['reportes', 'Reportes']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 text-[13px] border-b-2 -mb-px transition ${tab === k ? 'border-[#1c1c1c] font-medium text-[#1c1c1c]' : 'border-transparent text-[#8a8579] hover:text-[#3a3a3a]'}`}
            data-testid={`tab-${k}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'reportes' && <PushReports/>}

      {tab === 'pushes' && (<>
      <div className="bg-white border border-[#e8e5de] rounded-xl p-6 space-y-5">
        <div className="flex items-center gap-2">
          <Radio size={16} className="text-[#1c1c1c]"/>
          <h3 className="font-serif text-[22px]">Nuevo push general</h3>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-[11.5px] uppercase tracking-wider text-[#8a8579]">Fecha efectiva</Label>
            <Input type="date" value={form.effective_date} onChange={e => setForm({ ...form, effective_date: e.target.value })} className="mt-1 bg-[#fafaf6]" data-testid="input-push-date"/>
          </div>
          <div>
            <Label className="text-[11.5px] uppercase tracking-wider text-[#8a8579]">Hora efectiva (GT)</Label>
            <Input type="time" value={form.effective_time} onChange={e => setForm({ ...form, effective_time: e.target.value })} className="mt-1 bg-[#fafaf6]" data-testid="input-push-time"/>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-[11.5px] uppercase tracking-wider text-[#8a8579]">Fecha de notificación · opcional</Label>
            <Input type="date" value={form.notify_date} onChange={e => setForm({ ...form, notify_date: e.target.value })} className="mt-1 bg-[#fafaf6]" data-testid="input-push-notify-date"/>
          </div>
          <div>
            <Label className="text-[11.5px] uppercase tracking-wider text-[#8a8579]">Hora de notificación (GT)</Label>
            <Input type="time" value={form.notify_time} onChange={e => setForm({ ...form, notify_time: e.target.value })} className="mt-1 bg-[#fafaf6]" data-testid="input-push-notify-time"/>
            <p className="text-[10.5px] text-[#8a8579] mt-1">Si la dejas vacía, las estaciones lo ven de inmediato. Si la programas, el push queda oculto y silencioso hasta esa fecha/hora (debe ser antes de la efectiva).</p>
          </div>
        </div>

        <div>
          <Label className="text-[11.5px] uppercase tracking-wider text-[#8a8579]">Marcas destinatarias</Label>
          <div className="flex gap-2 mt-2">
            {['UNO', 'Shell'].map(b => (
              <button key={b} onClick={() => toggleBrand(b)} className={`px-4 py-2 rounded-full text-[12.5px] border transition ${form.target_brands.includes(b) ? 'bg-[#1c1c1c] text-[#f8f6f1] border-[#1c1c1c]' : 'bg-white text-[#3a3a3a] border-[#e8e5de]'}`} data-testid={`toggle-brand-${b}`}>
                {b}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 mt-2 cursor-pointer select-none text-[11.5px] text-[#3a3a3a]" data-testid="include-do-toggle">
            <input
              type="checkbox"
              checked={form.include_do}
              onChange={e => setForm({ ...form, include_do: e.target.checked })}
              className="accent-[#b45309] w-4 h-4"
            />
            Incluir estaciones DO (fuera de zona TM) — por default solo RBA reciben el push
          </label>
        </div>

        <div>
          <Label className="text-[11.5px] uppercase tracking-wider text-[#8a8579]">Productos con movimiento · marca ✓ y digita el nuevo precio base (Q)</Label>
          <p className="text-[11px] text-[#8a8579] mt-0.5">Solo los productos marcados llegan a las estaciones. Los demás conservan su último precio (tracking o último aprobado).</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-2">
            {PRODUCT_ORDER.map(k => {
              const on = form.products_on[k];
              return (
                <div key={k} className={`rounded-lg border p-2 ${on ? 'border-[#0f1d6f] bg-[#f5f8ff]' : 'border-[#e8e5de]'}`}>
                  <label className="flex items-center gap-1.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={e => setForm({
                        ...form,
                        products_on: { ...form.products_on, [k]: e.target.checked },
                        base_prices: e.target.checked ? form.base_prices : { ...form.base_prices, [k]: '' },
                      })}
                      className="accent-[#0f1d6f] w-4 h-4"
                      data-testid={`check-product-${k}`}
                    />
                    <span className={`text-[11px] ${on ? 'font-semibold text-[#0f1d6f]' : 'text-[#8a8579]'}`}>{PRODUCT_LABELS[k]}</span>
                  </label>
                  <Input
                    type="number" step="0.01" placeholder={on ? 'Q' : 'sin movimiento'}
                    value={form.base_prices[k]}
                    disabled={!on}
                    onChange={e => setForm({ ...form, base_prices: { ...form.base_prices, [k]: e.target.value } })}
                    className="mt-1.5 bg-white disabled:bg-[#f3f0e9] disabled:text-[#a8a397]"
                    data-testid={`input-base-${k}`}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-[11.5px] uppercase tracking-wider text-[#8a8579]">Nota pregrabada</Label>
            <Select value={form.preset_note_id || 'none'} onValueChange={v => setForm({ ...form, preset_note_id: v === 'none' ? '' : v })}>
              <SelectTrigger className="mt-1 bg-[#fafaf6]" data-testid="select-preset-note"><SelectValue placeholder="Sin nota pregrabada"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Sin nota pregrabada —</SelectItem>
                {allNotes.map(n => <SelectItem key={n.id} value={n.id}>{n.title} · {n.dept}</SelectItem>)}
              </SelectContent>
            </Select>
            {selectedNote && <p className="text-[11px] text-[#5a5a5a] mt-1 italic">"{selectedNote.text}"</p>}
          </div>
          <div>
            <Label className="text-[11.5px] uppercase tracking-wider text-[#8a8579]">Mensaje general</Label>
            <Textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Opcional…" className="mt-1 bg-[#fafaf6] min-h-[72px]" data-testid="textarea-message"/>
          </div>
        </div>

        <div className="flex justify-end">
          <Button disabled={sending} onClick={submit} className="bg-[#1c1c1c] text-[#f8f6f1] hover:bg-black" data-testid="btn-submit-push">
            {sending ? 'Enviando…' : 'Enviar push de precios'}
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="font-serif text-[22px]">Pushes recientes</h3>
          <div className="relative flex-1 min-w-[220px] max-w-[340px]">
            <SearchIcon size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a8a397]"/>
            <Input
              value={pushQuery}
              onChange={e => setPushQuery(e.target.value)}
              placeholder="Buscar pushes por estación…"
              className="bg-white pl-8 h-9 text-[12.5px] rounded-full"
              data-testid="push-list-search"
            />
          </div>
          <Select value={tmFilter} onValueChange={setTmFilter}>
            <SelectTrigger className="bg-white h-9 w-[200px] text-[12.5px] rounded-full" data-testid="push-list-tm-filter">
              <SelectValue placeholder="Filtrar por TM"/>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los TM</SelectItem>
              {tms.map(t => <SelectItem key={t.id} value={t.id} data-testid={`tm-filter-opt-${t.id}`}>{t.name}{t.own_brand ? ` · ${t.own_brand}` : ''}</SelectItem>)}
            </SelectContent>
          </Select>
          {listLoading && <Loader2 size={14} className="animate-spin text-[#8a8579]"/>}
          {(pushQuery.trim() || tmFilter !== 'all') && !listLoading && (
            <span className="text-[11.5px] text-[#8a8579]" data-testid="push-list-count">{pushes.length} push(es) con ese filtro</span>
          )}
        </div>
        {loading ? <Loader2 className="animate-spin text-[#8a8579]"/> : pushes.length === 0 ? (
          <p className="text-[13px] text-[#8a8579]" data-testid="push-list-empty">{(pushQuery.trim() || tmFilter !== 'all') ? 'Sin pushes que coincidan con el filtro.' : 'Aún no hay pushes.'}</p>
        ) : pushes.map(p => (
          <div key={p.id} className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden" data-testid={`push-row-${p.id}`}>
            <div className="w-full p-4 text-left flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Calendar size={13} className="text-[#8a8579]"/>
                  <p className="text-[13.5px] font-medium">Efectivo {fmt(p.effective_at)}</p>
                  {p.notify_at && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#fef3c7] text-[#92400e] font-medium" data-testid={`push-notify-at-${p.id}`}>
                      Notifica {fmt(p.notify_at)}
                    </span>
                  )}
                  <span className="text-[11px] text-[#8a8579]">· {(p.target_brands || []).join(' + ')}</span>
                </div>
                <p className="text-[11.5px] text-[#5a5a5a] mt-1">
                  {PRODUCT_ORDER.filter(k => p.base_prices && p.base_prices[k] !== undefined)
                    .map(k => `${PRODUCT_LABELS[k]} Q${p.base_prices[k]}${p.direction?.[k] === 'down' ? ' ▼' : p.direction?.[k] === 'up' ? ' ▲' : ''}`).join(' · ')}
                </p>
                {(p.below_suggested_count || 0) > 0 && (
                  <p className="text-[10.5px] mt-1">
                    <span className="px-2 py-0.5 rounded-full bg-[#ede9fe] text-[#5b21b6] font-medium" data-testid={`push-below-count-${p.id}`}>
                      {p.below_suggested_count} estación(es) por debajo de sugerido · solo recordatorio
                    </span>
                  </p>
                )}
                {(p.message || p.preset_note_title) && (
                  <p className="text-[11px] text-[#8a8579] mt-1 italic">{p.preset_note_title || ''}{p.preset_note_title && p.message ? ' · ' : ''}{p.message || ''}</p>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {p.rollback_of && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#e0e7ff] text-[#3730a3] font-medium uppercase tracking-wide">Rollback</span>}
                {p.rolled_back_at && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#fee2e2] text-[#991b1b] font-medium">Con rollback</span>}
                {!p.rollback_of && !p.rolled_back_at && (
                  <span
                    role="button"
                    onClick={(e) => { e.stopPropagation(); setRollbackTarget(p); }}
                    className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-[#991b1b]/40 text-[#991b1b] hover:bg-[#fee2e2] transition"
                    data-testid={`btn-rollback-${p.id}`}
                  >
                    <Undo2 size={11}/> Rollback
                  </span>
                )}
                {!p.rolled_back_at && (
                  <span
                    role="button"
                    title="Eliminar este envío (por error)"
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(p); }}
                    className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-[#991b1b]/40 text-[#991b1b] hover:bg-[#fee2e2] transition"
                    data-testid={`btn-delete-push-${p.id}`}
                  >
                    <Trash2 size={11}/> Eliminar
                  </span>
                )}
              </div>
            </div>
            <PushStations pushId={p.id} stations={p.stations || []} onPhoto={setPhotoSp}
              globalQuery={pushQuery} showSearch={p.source !== 'tracking_request'}/>
          </div>
        ))}
      </div>
      </>)}

      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md bg-[#f8f6f1]" data-testid="delete-push-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-[20px] flex items-center gap-2">
              <Trash2 size={16} className="text-[#991b1b]"/> Eliminar envío
            </DialogTitle>
          </DialogHeader>
          {deleteTarget && (
            <div className="space-y-3">
              <p className="text-[12.5px] text-[#5a5a5a]">
                Se eliminará por completo el push efectivo <strong>{fmtIso(deleteTarget.effective_at)}</strong>:
                desaparece de todas las estaciones y se borran sus notificaciones pendientes. Úsalo si el envío fue un error.
              </p>
              <p className="text-[11.5px] text-[#991b1b]">Si alguna estación ya confirmó con foto, no se podrá eliminar — usa Rollback para revertir precios.</p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDeleteTarget(null)} className="rounded-full text-[12.5px]" data-testid="delete-push-cancel">Cancelar</Button>
                <Button onClick={doDelete} disabled={deleting} className="rounded-full bg-[#991b1b] hover:bg-[#7f1616] text-white text-[12.5px]" data-testid="delete-push-confirm">
                  {deleting ? <Loader2 size={13} className="animate-spin mr-1.5"/> : <Trash2 size={13} className="mr-1.5"/>} Sí, eliminar envío
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!rollbackTarget} onOpenChange={(o) => { if (!o) setRollbackTarget(null); }}>
        <DialogContent className="max-w-md bg-[#f8f6f1]" data-testid="rollback-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-[20px] flex items-center gap-2"><Undo2 size={16}/> Rollback de push</DialogTitle>
          </DialogHeader>
          {rollbackTarget && (
            <div className="space-y-3">
              <p className="text-[13px] text-[#3a3a3a]">
                Se creará un <strong>push nuevo efectivo de inmediato</strong> que restaura en cada estación los <strong>precios anteriores</strong> al push del {fmtIso(rollbackTarget.effective_at)}.
              </p>
              <ul className="text-[12px] text-[#5a5a5a] list-disc pl-5 space-y-1">
                <li>Las confirmaciones pendientes del push original se cancelan.</li>
                <li>Cada estación deberá confirmar recepción y subir foto del monolito, como cualquier push.</li>
              </ul>
              <div className="flex gap-2 justify-end pt-1">
                <Button variant="outline" onClick={() => setRollbackTarget(null)} className="rounded-full text-[12.5px]" data-testid="rollback-cancel">Cancelar</Button>
                <Button onClick={doRollback} disabled={rollingBack} className="rounded-full bg-[#991b1b] hover:bg-[#7f1616] text-white text-[12.5px]" data-testid="rollback-confirm">
                  {rollingBack ? <Loader2 size={13} className="animate-spin mr-1.5"/> : <Undo2 size={13} className="mr-1.5"/>} Ejecutar rollback
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!photoSp} onOpenChange={(o) => { if (!o) setPhotoSp(null); }}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl bg-[#f8f6f1]" data-testid="push-photo-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-[18px]">Foto del monolito · {photoSp?.station_name}</DialogTitle>
          </DialogHeader>
          {photoSp && (
            <>
              <p className="text-[11.5px] text-[#5a5a5a] -mt-1">Confirmado {fmt(photoSp.confirmed_at)}{photoSp.confirmed_by_name ? ` · ${photoSp.confirmed_by_name}` : ''}</p>
              <ZoomableImage src={photoURL(photoSp.photo_url)} alt={`Monolito ${photoSp.station_name}`} maxH="65vh" testId="push-photo-zoom"/>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPushPrices;
